import {
  PaginatedParams,
  FilterParams,
  PaginatedData
} from "src/library/utils";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  IUser,
  IUserGroup
} from "src/core/users/api/users.api";
import { CreditRequestRowType } from "src/modules/credit-request/context/CreditRequestContext";
import { IDrop } from "src/core/drops/api/drops.api";
import { IOrder } from "src/core/orders/api/orders.api";
import { ICreditRequest } from "src/modules/credit-request/api/credit-request.api";
import { RoleType } from "src/core/shared/api/roles.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

// Entity types

export enum SupportTicketStatus {
  New = "new",
  PendingAdminResponse = "pending_admin_response",
  PendingCustomerResponse = "pending_customer_response",
  AdminResolved = "resolved_by_admin",
  CustomerResolved = "resolved_by_customer"
}
export interface ISupportTicket {
  id: number;
  body: string;
  userId: number;
  branchId: number | null;
  orderId: number | null;
  supportTicketTypeId: number;
  status: SupportTicketStatus;
  creditStatus?: CreditStatus | null;
  createdAt: Date;
  updatedAt: Date;
  credits?: ICreditRequest[];
  order?: IOrder;
  user?: IUser;
  branch?: IDrop;
  supportTicketType?: ISupportTicketType;
  currentUserSubscribed?: boolean;
  assignedUserId?: number | null;
  assignedUser?: IUser;
  userGroupId?: number | null;
  userGroup?: IUserGroup;
  dateOfContact: Date;
  origin?: Origin | null;
  totalTimeTaken?: number;
  timeTaken?: ISupportTicketTimeTaken[];
  priority?: SupportTicketPriority;
  resolvedAt?: Date;
}

export enum Origin {
  Phone = "phone",
  Email = "email",
  /** Ticket origin was raised by a customer user */
  Customer = "customer"
}

/** Priority level associated with a support ticket */
export enum SupportTicketPriority {
  Low = "low",
  Medium = "medium",
  High = "high",
  Urgent = "urgent"
}

export enum CreditStatus {
  Reviewed = "reviewed",
  AutoReviewed = "auto_reviewed",
  RequiresReview = "requires_review",
  Resolved = "resolved"
}

export type SupportTicketWithType = ISupportTicket & {supportTicketType: Pick<ISupportTicketType, "id" | "name">};

export interface ISupport {
  id: number;
  title: string;
  s3Key?: string;
  description?: string;
  type: SupportType;
}

export interface ISupportTicketType {
  id: number;
  name: string;
  description: string;
  options: SupportTicketTypeOptions
}

export interface ISupportTicketResponseTemplate {
  id: number;
  name: string;
  message: string;
  options: SupportTicketResponseTemplateOptions
}

export interface Validation {
  /** If provided, adds validation to check presence of input @default false */
  required?: boolean;
  /** If provided, adds validation to check field input matches the supplied regex */
  regex?: string;
  /** If provided, controls the message in case of a failed regex match */
  regexMessage?: string;
  /** If provided, adds validation for the lower bound of a number input */
  min?: number;
   /** If provided, adds validation for the upper bound of a number input */
  max?: number;
}

export interface ISupportTicketExtraDataFormQuestion {
  /** Unique key for the form input */
  key: string;
  /** Display label for the form input */
  label: string;
  /** Input type for the question */
  type: "text" | "number" | "email";
  /** Type of form field */
  formFieldType: "input" | "textarea" | "select" | "attachments";
  /** Validation rules */
  validation: Validation;
  /** For form field types of select, this controls the available options */
  options?: string[];
  /** Title for use in attachments if needed */
  title?: string;
  /** Description currently used for custom elaboration as to what attachments are needed */
  description?: string;
}

export interface SupportTicketTypeOptions {
  order?: boolean | {
    statusCodes: string[];
  };
  /** This needs to be set in order to view/note products when raising support tickets */
  allowCredits?: boolean | SupportTicketCreditOptions;
  adminView?: boolean;
  extraData?: Array<{
    key: string;
    required: boolean;
    type: "text" | "long_text" | "number";
  }>;
  /** Used to power additional data gathering for a support ticket submission */
  formQuestions?: ISupportTicketExtraDataFormQuestion[];
  /** If false, should hide the wysiwyg editor in the raise support ticket UI */
  showWysiwyg?: boolean
  /** Holds config and data for displaying an FAQ style answer for a support ticket type. */
  faq?: {
    /** If true, the stored FAQ response should be displayed */
    enabled: boolean;
    /** If true, the user should be able to raise a ticket after viewing the stored FAQ response */
    raiseTicketEnabled: boolean;
    /** The HTML FAQ title text to show when faq.enabled is set to true */
    title?: string;
    /** The HTML FAQ body text to show to the user when faq.enabled is set to true */
    body: string;
  }
}

export interface SupportTicketResponseTemplateOptions {
  /** If true, there is an order associated with the support ticket */
  order?: boolean;
  /** If true, credits are allowed to be given in response to the support ticket */
  allowCredits?: boolean | SupportTicketCreditOptions;
}

export interface SupportTicketCreditOptions {
  creditWholeOrder: boolean;
  reasonCode?: string;
  requireEvidence?: boolean;
  /** Valid delivery window config */
  validDeliveryWindow: {
    /** The minimum number of days to pass after the delivery date before a credit can be raised,
     * e.g 1 = allow credit 1 day after delivery
     */
    minDaysAfterDelivery: number;
    /** The maximum number of days to pass after the delivery date where a credit can be raised
     *  e.g 7 = allow 7 days for the credit to be raised
     */
    maxDaysAfterDelivery: number;
    /** If order falls outside of the range specified by validDeliveryWindow.days, show this error message */
    errorMessage?: string;
  };
}

export interface ISupportTicketDocument {
  fileName: string;
  folderId: number;
  id: number;
  key: string;
  mimeType: string;
  size: string;
  slug: string;
}

export interface ISupportTicketMessage {
  id: number;
  message: string;
  userId: number;
  supportTicketId: number;
  createdAt: Date;
  documents?: ISupportTicketDocument[];
  internal?: boolean;
}

export type SupportType = "VIDEO" | "PDF";
/** value in minutes ie 90 for 1.5 hours */
export type TimeTaken = number;

// request + response types. evidence is an array of s3 file keys eg []
export type CreateSupportTicketParams = Pick<ISupportTicket, "supportTicketTypeId" | "branchId" | "body" | "userId">
  & Partial<Pick<ISupportTicket, "orderId" | "origin" | "dateOfContact">>
  & {
  credits?: CreditRequestRowType[];
  evidence?: string[];
  timeTaken?: TimeTaken;
};

export interface GetSupportTicketParams {
  supportTicketId: number;
}

export interface CloseSupportTicketParams {
  supportTicketId: number;
  message?: string;
  timeTaken?: TimeTaken;
  maintainCredits?: boolean;
}

export interface UpdateSupportTicketParams extends Partial<Pick<ISupportTicket, "userGroupId" | "assignedUserId">> {
  supportTicketId: number;
  message?: string;
  internalMessage?: string;
  credits?: CreditRequestRowType[];
  submitReview?: boolean;
  resolved: boolean;
  evidence?: string[];
  unsubscribeUserId?: number;
  timeTaken?: TimeTaken;
  // Property to update priority level of the support ticket
  priority?: SupportTicketPriority;
}

export interface ISupportTicketTimeTaken {
  id: number;
  createdAt: Date;
  userId: number;
  supportTicketId: number;
  timeTaken: number;
  user?: IUser;
}

export interface ListSupportTicketParams {
  pagination: PaginatedParams<ISupportTicket>;
  filters: FilterParams<Pick<ISupportTicket, "status" | "updatedAt" | "supportTicketTypeId" | "assignedUserId" | "userGroupId"> & {
    branchNumber: string;
    orderDeliveryDate: Date;
  }>;
}

export interface ListBranchSupportTicketParams {
  branchId: number;
  pagination: PaginatedParams<ISupportTicket>;
  filters: FilterParams<Pick<ISupportTicket, "status" | "updatedAt" | "supportTicketTypeId"> & {orderDeliveryDate: Date }>;
}

export interface ListTicketMessagesParams {
  supportTicketId: number;
  pagination: PaginatedParams<ISupportTicketMessage>;
  currentUserRole?: RoleType;
  filters?: FilterParams<Pick<ISupportTicketMessage, "internal">>
}

export interface ListOrderSupportTicketParams {
  orderId: number;
  pagination: PaginatedParams<SupportTicketWithType>;
}

export interface StringResponse {
  message: string;
}

export type ListTicketMessagesResponse = PaginatedData<ISupportTicketMessage & {user?: IUser}>;

type SupportEndpointConfig = {
  LIST_SUPPORT_DOCUMENTS: EndpointConfig<
    null,
    ISupport[]
  >;
  CREATE_SUPPORT_TICKET: EndpointConfig<
    CreateSupportTicketParams,
    ISupportTicket
  >;
  UPDATE_SUPPORT_TICKET: EndpointConfig<
    UpdateSupportTicketParams,
    ISupportTicket
  >;
  LIST_SUPPORT_TICKETS: EndpointConfig<
    ListSupportTicketParams,
    PaginatedData<ISupportTicket>
  >;
  GET_SUPPORT_TICKET: EndpointConfig<
    GetSupportTicketParams,
    ISupportTicket
  >;
  GET_SUPPORT_TICKET_RESPONSE_TEMPLATES: EndpointConfig<
    null,
    ISupportTicketResponseTemplate[]
  >;
  CLOSE_SUPPORT_TIKCET: EndpointConfig<
  CloseSupportTicketParams,
  ISupportTicket
>;
  LIST_BRANCH_TICKETS: EndpointConfig<
    ListBranchSupportTicketParams,
    PaginatedData<ISupportTicket>
  >;
  LIST_ORDER_TICKETS: EndpointConfig<
    ListOrderSupportTicketParams,
    PaginatedData<SupportTicketWithType>
  >;
  LIST_TICKET_TYPES: EndpointConfig<
    null,
    ISupportTicketType[]
  >;
  LIST_TICKET_MESSAGES: EndpointConfig<
    ListTicketMessagesParams,
    ListTicketMessagesResponse
  >;
};

// supportTicket api specific logic

const BASE_URL = `${process.env.REACT_APP_API_URL}/support`;

const requests: RequestConfig<SupportEndpointConfig> = {
  LIST_SUPPORT_DOCUMENTS: () => ({
    url: `${BASE_URL}/documents`,
    method: "GET"
  }),

  CREATE_SUPPORT_TICKET: data => ({
    url: `${BASE_URL}/tickets`,
    method: "POST",
    data
  }),
  UPDATE_SUPPORT_TICKET: ({ supportTicketId, ...data }) => ({
    url: `${BASE_URL}/tickets/${supportTicketId}`,
    method: "PATCH",
    data
  }),
  LIST_SUPPORT_TICKETS: params => ({
    url: `${BASE_URL}/tickets`,
    method: "GET",
    params
  }),
  GET_SUPPORT_TICKET: ({ supportTicketId }) => ({
    url: `${BASE_URL}/tickets/${supportTicketId}`,
    method: "GET"
  }),
  GET_SUPPORT_TICKET_RESPONSE_TEMPLATES: () => ({
    url: `${BASE_URL}/ticket-response-templates`,
    method: "GET",
    cache: { maxAge: cacheMinutesToMilliseconds(60) }
  }),
  CLOSE_SUPPORT_TIKCET: ({
    supportTicketId, message, timeTaken, maintainCredits
  }) => ({
    url: `${BASE_URL}/tickets/${supportTicketId}`,
    method: "DELETE",
    data: {
      message,
      timeTaken,
      maintainCredits
    }
  }),
  LIST_BRANCH_TICKETS: ({ branchId, ...params }) => ({
    url: `${BASE_URL}/tickets/branches/${branchId}`,
    method: "GET",
    params
  }),
  LIST_ORDER_TICKETS: ({ orderId, ...params }) => ({
    url: `${BASE_URL}/tickets/orders/${orderId}`,
    method: "GET",
    params
  }),
  LIST_TICKET_TYPES: () => ({
    url: `${BASE_URL}/tickets/ticket-types`,
    method: "GET"
  }),

  LIST_TICKET_MESSAGES: ({
    supportTicketId, currentUserRole, ...params
  }) => {
    const requestParams: Omit<ListTicketMessagesParams, "currentUserRole"> = {
      pagination: params.pagination,
      supportTicketId,
      filters: {}
    };

    // Ensure if current user is not admin, we apply filter to not include internal notes!
    if (currentUserRole !== "admin") {
      requestParams.filters = { internal: { $not: true } };
    }

    return {
      url: `${BASE_URL}/tickets/${supportTicketId}/messages`,
      method: "GET",
      params: requestParams
    };
  }
};

export const useSupportApi: UseApiRequest<SupportEndpointConfig> = name => useAxios(requests[ name ], name);
